import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import PostCard from "@/components/community/PostCard";
import CreatePostDialog from "@/components/community/CreatePostDialog";
import { MessagesSquare, Plus } from "lucide-react";

// Community discussions bound to an artist (or festival) via slug. Threads created on first interaction only.
export default function ArtistDiscussions({ artistSlug, festivalSlug, defaultCategory = "music_artists" }) {
  const { t } = useLang();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(false);
  const params = artistSlug ? { artist_slug: artistSlug } : { festival_slug: festivalSlug };
  const key = artistSlug ? ["artistDisc", artistSlug] : ["festivalDisc", festivalSlug];

  const { data: categories = [] } = useQuery({ queryKey: ["comCats"], queryFn: async () => (await api.get("/community/categories")).data });
  const { data } = useQuery({ queryKey: key, queryFn: async () => (await api.get("/community/posts", { params: { ...params, limit: 20 } })).data });

  const link = artistSlug ? { artist_slug: artistSlug } : { festival_slug: festivalSlug };
  const openCreate = () => { if (!user) { toast.error(t("com_login_required")); return; } setDialog(true); };

  return (
    <section className="mt-12" data-testid="artist-discussions">
      <div className="flex items-center justify-between mb-5 border-b border-amber-900/30 pb-4">
        <h2 className="font-serif text-2xl text-stone-100 flex items-center gap-2"><MessagesSquare className="w-5 h-5 text-amber-500" /> {t("com_artist_discussions")} <span className="font-mono text-sm text-stone-500">({data?.total || 0})</span></h2>
        <button data-testid="artist-create-discussion" onClick={openCreate} className="inline-flex items-center gap-1.5 bg-[#181A20] text-amber-400 border border-amber-600/40 px-4 py-2 rounded-md text-sm font-medium hover:bg-[#22252D]"><Plus className="w-4 h-4" /> {t("com_create_discussion")}</button>
      </div>
      {!data?.posts?.length ? (
        <div className="text-center py-10 text-stone-500 border border-dashed border-[#272A34] rounded-lg" data-testid="artist-no-discussions">{t("com_no_discussions")}</div>
      ) : (
        <div className="space-y-3">{data.posts.map((p) => <PostCard key={p.id} post={p} />)}</div>
      )}
      <CreatePostDialog open={dialog} onClose={() => setDialog(false)} categories={categories} defaultCategory={defaultCategory} link={link} onCreated={() => qc.invalidateQueries({ queryKey: key })} />
    </section>
  );
}
